/**
 * Confirmation dialog shown ON TOP of the drawer when saving an edited interview.
 * Shows which invites/emails will be sent, with a "Note to guests" textarea.
 *
 * 4 variants based on what changed:
 * - Invites only
 * - Emails only
 * - Both invites and emails
 * - No communication changes → dialog not shown, save directly
 */
import { useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Textarea,
  Avatar,
  AvatarGroup,
} from '@chakra-ui/react';
import { Button } from '@kula-ai/components';
import { SCHEDULE_INTERVIEW_COPY } from '../config/schedule.copy';

export interface RecipientInfo {
  name: string;
  avatar?: string;
}

export interface SaveConfirmationProps {
  /** What type of updates are being sent. */
  variant: 'invites' | 'emails' | 'invites-and-emails';
  /** Candidate receiving updated invites/emails. */
  candidate: RecipientInfo;
  /** Whether the candidate is in the invite recipients list. */
  showCandidateInvite?: boolean;
  /** Whether the interviewer(s) are in the invite recipients list. */
  showInterviewerInvite?: boolean;
  /** Whether the candidate is in the email recipients list. */
  showCandidateEmail?: boolean;
  /** Whether the interviewer(s) are in the email recipients list. */
  showInterviewerEmail?: boolean;
  /** Interviewers receiving updated invites. */
  interviewerInviteRecipients?: RecipientInfo[];
  /** Interviewers receiving updated emails. */
  interviewerEmailRecipients?: RecipientInfo[];
  /** Called with the note text when user confirms. */
  onConfirm: (_note: string) => void;
  /** Called when user cancels — dialog dismissed, drawer stays open. */
  onCancel: () => void;
  isLoading?: boolean;
}

export const SaveConfirmationDialog = ({
  variant,
  candidate,
  showCandidateInvite = true,
  showInterviewerInvite = true,
  showCandidateEmail = true,
  showInterviewerEmail = true,
  interviewerInviteRecipients = [],
  interviewerEmailRecipients = [],
  onConfirm,
  onCancel,
  isLoading = false,
}: SaveConfirmationProps) => {
  const [note, setNote] = useState('');
  const copy = SCHEDULE_INTERVIEW_COPY.editDialogs;

  const title =
    variant === 'invites'
      ? copy.saveInvites.title
      : variant === 'emails'
        ? copy.saveEmails.title
        : copy.saveInvitesAndEmails.title;

  const showInvitesSection =
    variant === 'invites' || variant === 'invites-and-emails';
  const showEmailsSection =
    variant === 'emails' || variant === 'invites-and-emails';

  return (
    <Flex
      position="absolute"
      inset="0"
      zIndex="modal"
      align="center"
      justify="center"
      bg="blackAlpha.400"
    >
      <Box
        bg="white"
        borderRadius="lg"
        boxShadow="xl"
        maxW="480px"
        w="100%"
        p="6"
      >
        <Text fontSize="md" fontWeight="semibold" mb="4">
          {title}
        </Text>

        {showInvitesSection && (
          <Box mb="4">
            <Text fontSize="sm" color="secondary.600" mb="2">
              {copy.saveInvites.invitesLabel}
            </Text>
            {showCandidateInvite && (
              <RecipientRow
                label={copy.candidateLabel}
                recipients={[candidate]}
              />
            )}
            {showInterviewerInvite &&
              interviewerInviteRecipients.length > 0 && (
                <RecipientRow
                  label={copy.interviewersLabel}
                  recipients={interviewerInviteRecipients}
                />
              )}
          </Box>
        )}

        {showEmailsSection && (
          <Box mb="4">
            <Text fontSize="sm" color="secondary.600" mb="2">
              {copy.saveEmails.emailsLabel}
            </Text>
            {showCandidateEmail && (
              <RecipientRow
                label={copy.candidateLabel}
                recipients={[candidate]}
              />
            )}
            {showInterviewerEmail && interviewerEmailRecipients.length > 0 && (
              <RecipientRow
                label={copy.interviewersLabel}
                recipients={interviewerEmailRecipients}
              />
            )}
          </Box>
        )}

        <Box mb="4">
          <Text fontSize="sm" fontWeight="medium" mb="2">
            {copy.noteToGuests.label}
          </Text>
          <Textarea
            placeholder={copy.noteToGuests.placeholder}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            resize="none"
          />
        </Box>

        <Flex justifyContent="flex-end" gap="3">
          <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
            {SCHEDULE_INTERVIEW_COPY.footer.cancel}
          </Button>
          <Button
            variant="primary"
            onClick={() => onConfirm(note)}
            disabled={isLoading}
          >
            {SCHEDULE_INTERVIEW_COPY.footer.saveInterview}
          </Button>
        </Flex>
      </Box>
    </Flex>
  );
};

// ---------------------------------------------------------------------------
// Internal
// ---------------------------------------------------------------------------

function RecipientRow({
  label,
  recipients,
}: {
  label: string;
  recipients: RecipientInfo[];
}) {
  return (
    <Flex align="center" gap="2" mb="1">
      <Text fontSize="sm" color="secondary.500" minW="fit-content">
        {label}
      </Text>
      {recipients.length <= 3 ? (
        <Flex align="center" gap="2" flexWrap="wrap">
          {recipients.map((r) => (
            <Flex key={r.name} align="center" gap="1">
              <Avatar size="xs" name={r.name} src={r.avatar} />
              <Text fontSize="sm">{r.name}</Text>
            </Flex>
          ))}
        </Flex>
      ) : (
        <AvatarGroup size="xs" max={4}>
          {recipients.map((r) => (
            <Avatar key={r.name} name={r.name} src={r.avatar} />
          ))}
        </AvatarGroup>
      )}
    </Flex>
  );
}
